import { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchProfile, UserProfile } from '../api/viewProfileApiService';
import {
  getStatusLabel,
  getStatusBadgeClasses,
  StatusValue,
} from '@/shared/utils/statusBadge';

// ============================================================================
// Constants
// ============================================================================

export const ADMIN_PROFILE_NAV_ITEMS = [
  { id: 'basic', label: 'Podstawowe informacje' },
  { id: 'password', label: 'Zmiana has\u0142a' },
];

export const CLIENT_PROFILE_NAV_ITEMS = [
  { id: 'basic', label: 'Podstawowe informacje' },
  { id: 'permissions', label: 'Uprawnienia' },
  { id: 'password', label: 'Zmiana has\u0142a' },
];

export const TECHNICIAN_PROFILE_NAV_ITEMS = [
  { id: 'basic', label: 'Podstawowe informacje' },
  { id: 'permissions', label: 'Uprawnienia' },
  { id: 'password', label: 'Zmiana has\u0142a' },
];

// ============================================================================
// Utility
// ============================================================================

export const getUserInitials = (name: string | null | undefined): string => {
  if (!name) {
    return '';
  }
  const names = name.trim().split(' ').filter(Boolean);
  if (names.length >= 2) {
    const first = names[0];
    const last = names[names.length - 1];
    if (first?.[0] && last?.[0]) {
      return `${first[0]}${last[0]}`.toUpperCase();
    }
  }
  return name.substring(0, 2).toUpperCase();
};

const getRoleLabel = (role: string | null): string => {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'Super Administrator';
    case 'ADMIN':
      return 'Administrator';
    case 'CLIENT':
      return 'Klient';
    case 'TECHNICIAN':
      return 'Technik';
    default:
      return '\u2014';
  }
};

// ============================================================================
// Hook Return Type
// ============================================================================

export interface UseViewProfileReturn {
  profileData: UserProfile | undefined;
  isLoading: boolean;
  showErrorScreen: boolean;
  name: string;
  initials: string;
  profileImageUrl: string | undefined;
  statusLabel: string;
  statusBadgeClasses: { container: string; dot: string };
  roleLabel: string;
  role: string | null;
  isClient: boolean;
  isTechnician: boolean;
  navItems: { id: string; label: string }[];
  refetch: () => Promise<void>;
}

// ============================================================================
// Hook
// ============================================================================

export const useViewProfile = (): UseViewProfileReturn => {
  const [profileData, setProfileData] = useState<UserProfile>();
  const [isLoading, setIsLoading] = useState(true);
  const [showErrorScreen, setShowErrorScreen] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchProfile();
      if (result.success && result.data) {
        setProfileData(result.data);
        setShowErrorScreen(false);
      } else {
        setShowErrorScreen(true);
      }
    } catch {
      setShowErrorScreen(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Computed display values
  const name = profileData?.name ?? '\u2014';

  const initials = useMemo(
    () => getUserInitials(name === '\u2014' ? null : name),
    [name]
  );

  const profileImageUrl = useMemo(
    () => profileData?.profilePicture?.url?.trim() || undefined,
    [profileData]
  );

  const status = useMemo((): StatusValue | undefined => {
    if (!profileData) {
      return undefined;
    }
    return (
      profileData.status === 'draft' ? 'inactive' : profileData.status
    ) as StatusValue;
  }, [profileData]);

  const statusLbl = useMemo(() => getStatusLabel(status), [status]);
  const statusBadgeCls = useMemo(() => getStatusBadgeClasses(status), [status]);
  const role = profileData?.role ?? null;
  const roleLabel = useMemo(() => getRoleLabel(role), [role]);
  const isClient = role === 'CLIENT';
  const isTechnician = role === 'TECHNICIAN';

  const navItems = useMemo(
    () =>
      isClient
        ? [...CLIENT_PROFILE_NAV_ITEMS]
        : isTechnician
          ? [...TECHNICIAN_PROFILE_NAV_ITEMS]
          : [...ADMIN_PROFILE_NAV_ITEMS],
    [isClient, isTechnician]
  );

  return {
    profileData,
    isLoading,
    showErrorScreen,
    name,
    initials,
    profileImageUrl,
    statusLabel: statusLbl,
    statusBadgeClasses: statusBadgeCls,
    roleLabel,
    role,
    isClient,
    isTechnician,
    navItems,
    refetch: fetchData,
  };
};
